/**
 * @fileoverview Eligible-departments query hook.
 * @description Wraps GET /users/eligible-departments?roleIds=… — the departments a new user with
 * the chosen role may be placed in, for the invitation forms' Department picker. A Sales role is
 * fixed to the SALES department; any other role may offer a choice, with a default when there's
 * exactly one sensible option. Disabled until a role is picked.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useQuery } from '@tanstack/react-query'

import { getEligibleDepartments } from '@/features/user-invitations/services/user-invitations.service'

import type { EligibleDepartments } from '@/features/user-invitations/types/user-invitations.types'
import type { ApiError } from '@/shared/types/http.types'

/**
 * @description The department to preselect: the fixed one, else the default, else none.
 * @param {EligibleDepartments | undefined} departments - The eligible departments, once loaded.
 * @returns {string} The department id, or '' when the inviter must choose.
 */
export function preselectedDepartmentId(departments: EligibleDepartments | undefined): string {
  return departments?.fixedDepartmentId ?? departments?.defaultDepartmentId ?? ''
}

/**
 * @description Fetches the eligible departments for a role.
 * @param {string} roleId - The role being invited ('' while none is picked).
 * @param {boolean} enabled - Whether the form is open.
 * @returns {import('@tanstack/react-query').UseQueryResult<EligibleDepartments, ApiError>} The
 * query result.
 */
export function useEligibleDepartments(roleId: string, enabled: boolean) {
  return useQuery<EligibleDepartments, ApiError>({
    queryKey: ['users', 'eligible-departments', roleId],
    queryFn: () => getEligibleDepartments([roleId]),
    enabled: enabled && roleId.length > 0,
  })
}
